import { motion } from 'motion/react'
import type { ReactNode } from 'react'
import { cn } from '../lib/utils'
import { BrandMark, ThemeToggle, type ShellBrand } from './app-shell'
import {
  AuthIllustration,
  AuthMiniArt,
  FeatureRotator,
  TrustBadges,
  useMediaQuery,
  type AuthFeature,
} from './auth-art'

const ease = [0.16, 1, 0.3, 1] as const

/**
 * Split-screen sign-in layout. The brand panel (aurora in the tenant's
 * colour, an animated illustration, rotating highlights) sits beside a glass
 * form card. Below `lg` the panel becomes a compact animated header band and
 * the card overlaps it.
 *
 * Everything takes its colour from the brand ramp, so a white-labelled tenant
 * gets its own palette and name and no Bixo branding.
 */
export function AuthLayout({
  brand,
  title,
  subtitle,
  children,
  showcase,
  footer,
  features,
  badges,
  header,
  illustration = true,
  size = 'sm',
}: {
  brand: ShellBrand
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  /** Headline for the brand panel. */
  showcase: ReactNode
  footer?: ReactNode
  /** Highlights that rotate under the illustration. */
  features?: AuthFeature[]
  badges?: { icon: AuthFeature['icon']; label: string }[]
  /** Above the title inside the card (e.g. a step progress bar). */
  header?: ReactNode
  illustration?: boolean
  /** `lg` widens the card for multi-step flows. */
  size?: 'sm' | 'lg'
}) {
  const wide = useMediaQuery('(min-width: 1024px)')

  return (
    <div
      className={cn(
        'grid min-h-dvh bg-background',
        size === 'lg'
          ? 'lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]'
          : 'lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]',
      )}
    >
      <aside className="relative hidden overflow-hidden bg-brand-950 text-white lg:flex lg:flex-col lg:p-10 xl:p-12">
        <div className="auth-aurora" aria-hidden />
        <div
          className="bg-grid absolute inset-0 [mask-image:radial-gradient(70%_60%_at_50%_40%,black,transparent)] opacity-20 mix-blend-overlay"
          aria-hidden
        />
        <div
          className="auth-float-slow absolute top-[18%] -right-16 size-56 rounded-full border border-white/10"
          aria-hidden
        />
        <div
          className="auth-float absolute -bottom-20 -left-10 size-72 rounded-full bg-white/5 blur-2xl"
          aria-hidden
        />

        <div className="relative [&_p]:text-white">
          <BrandMark brand={brand} onDark />
        </div>

        <div className="relative flex flex-1 flex-col justify-center gap-8 py-8">
          <motion.div
            className="max-w-xl"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
          >
            {showcase}
          </motion.div>
          {illustration && wide && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease, delay: 0.1 }}
              className="mx-auto w-full max-w-[520px] [@media(max-height:820px)]:max-w-[400px]"
            >
              <AuthIllustration />
            </motion.div>
          )}
          {features && features.length > 0 && <FeatureRotator features={features} />}
        </div>

        <div className="relative flex flex-wrap items-center justify-between gap-3">
          {badges && badges.length > 0 ? <TrustBadges items={badges} /> : <span />}
          <p className="text-xs text-white/55">
            © {new Date().getFullYear()} {brand.name}
          </p>
        </div>
      </aside>

      <main className="relative flex min-w-0 flex-col overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 hidden bg-[radial-gradient(55%_45%_at_85%_0%,color-mix(in_oklch,var(--color-brand-500)_12%,transparent),transparent_70%)] lg:block"
          aria-hidden
        />

        {/* Mobile header band */}
        <div className="relative overflow-hidden rounded-b-[28px] bg-brand-950 px-5 pt-5 pb-16 text-white lg:hidden">
          <div className="auth-aurora" aria-hidden />
          <div className="relative flex items-center justify-between [&_button]:text-white [&_button:hover]:bg-white/10 [&_p]:text-white">
            <BrandMark brand={brand} onDark />
            <ThemeToggle />
          </div>
          {illustration && <AuthMiniArt className="relative mt-4" />}
        </div>

        <div className="relative hidden justify-end px-8 pt-6 lg:flex">
          <ThemeToggle />
        </div>

        <div className="relative -mt-10 flex flex-1 items-start justify-center px-4 pb-8 sm:px-8 lg:mt-0 lg:items-center lg:py-10">
          <motion.div
            className={cn(
              'w-full rounded-2xl border border-border/70 bg-surface/90 p-6 shadow-elevated backdrop-blur-xl sm:p-8',
              'dark:bg-surface/70 dark:shadow-[0_30px_80px_-40px_var(--color-brand-700)]',
              size === 'lg' ? 'max-w-2xl' : 'max-w-md',
            )}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease }}
          >
            {header}
            <h1 className="text-2xl font-semibold tracking-tight text-balance">{title}</h1>
            {subtitle && <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>}
            <div className="mt-7">{children}</div>
          </motion.div>
        </div>

        {footer && (
          <div className="relative px-4 pb-6 text-center text-xs text-muted-foreground">{footer}</div>
        )}
      </main>
    </div>
  )
}
